import { useState, useEffect, useCallback, useContext } from 'react';
import {
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  type PushSource,
} from '../services/pushNotification';
import { AuthContext } from '../contexts/auth-context';
import {
  ensureRegistration,
  refreshPushState,
  setRegistration,
  usePushState,
} from '../utils/push-state';

export interface PushNotificationState {
  isSupported: boolean;
  /** Push is on for the signed-in account on this device. */
  isEnabled: boolean;
  isLoading: boolean;
  permission: NotificationPermission;
  needsAppInstall: boolean;
  isIOSOrIPadOS: boolean;
  isMacOS: boolean;
  isAppInstalled: boolean;
}

export interface UsePushNotificationsReturn extends PushNotificationState {
  enableNotifications: (source?: PushSource) => Promise<void>;
  disableNotifications: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  error: string | null;
}

/**
 * Push on this device for the signed-in account, read from the shared store
 * (`utils/push-state`). Works outside an AuthProvider too, as plain "this
 * browser is subscribed".
 */
export function usePushNotifications(): UsePushNotificationsReturn {
  const push = usePushState();
  const auth = useContext(AuthContext);
  const userId = auth?.user?.id != null ? String(auth.user.id) : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Until the server has answered for this account, the browser's own
  // subscription stands in.
  const registration = push.registration;
  const registeredHere = userId && registration?.userId === userId && registration.endpoint === push.endpoint
    ? registration.registered
    : push.endpoint !== null;
  const isEnabled = push.isSupported && push.permission === 'granted' && registeredHere;

  const refreshStatus = useCallback(async () => {
    setError(null);
    await refreshPushState();
  }, []);

  // Enable push notifications. Asking the browser comes first, with nothing
  // awaited before it: Safari and Firefox ignore a permission request that is
  // not made straight from the click.
  const enableNotifications = useCallback(async (source?: PushSource) => {
    setError(null);
    setBusy(true);
    try {
      if (push.needsAppInstall && push.permission !== 'granted') {
        throw new Error('Please install the app first to enable notifications on iOS');
      }

      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission was denied');
      }

      const subscription = await subscribeToPushNotifications(source);
      if (userId) setRegistration(userId, subscription.endpoint, true);
      await refreshPushState();
      await auth?.refreshProfile();
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to enable notifications');
      // A "Block" in the browser's prompt has to show everywhere as blocked.
      await refreshPushState();
      throw err;
    } finally {
      setBusy(false);
    }
  }, [push.needsAppInstall, push.permission, userId, auth]);

  // Disable push for this account on this device; other accounts keep theirs.
  const disableNotifications = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await unsubscribeFromPushNotifications();
      if (userId) setRegistration(userId, push.endpoint, false);
      await refreshPushState();
      await auth?.refreshProfile();
    } catch (err) {
      console.error('Failed to disable push notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to disable notifications');
      throw err;
    } finally {
      setBusy(false);
    }
  }, [userId, push.endpoint, auth]);

  useEffect(() => {
    void refreshPushState();
  }, []);

  useEffect(() => {
    if (userId && push.endpoint) void ensureRegistration(userId);
  }, [userId, push.endpoint]);

  return {
    isSupported: push.isSupported,
    isEnabled,
    isLoading: push.loading || busy,
    permission: push.permission,
    needsAppInstall: push.needsAppInstall,
    isIOSOrIPadOS: push.isIOSOrIPadOS,
    isMacOS: push.isMacOS,
    isAppInstalled: push.isAppInstalled,
    enableNotifications,
    disableNotifications,
    refreshStatus,
    error
  };
}
